import { defineStore } from "pinia";
import Vue from "vue";
import RATINGS_QUERY from "@/graphql/pageData/ratings.graphql";
import PRICE_OFFER_PERMISSION_QUERY from "@/graphql/pageData/priceOfferPermission.graphql";
import QUESTION_SUBSCRIPTIONS_QUERY from "@/graphql/pageData/questionSubscriptions.graphql";
import MY_PRICES_QUERY from "@/graphql/pageData/myPrices.graphql";
import REFERENCE_PHOTOS_QUERY from "@/graphql/pageData/referencePhotos.graphql";
import MY_TENDERS_QUERY from "@/graphql/pageData/myTenders.graphql";
import CREATE_OR_UPDATE_PRICE from "@/graphql/mutations/createOrUpdatePrice.graphql";
import DELETE_PRICE from "@/graphql/mutations/deletePrice.graphql";
import UPLOAD_REFERENCE_PHOTO from "@/graphql/mutations/uploadReferencePhoto.graphql";
import UPDATE_REFERENCE_PHOTO from "@/graphql/mutations/updateReferencePhoto.graphql";
import DELETE_REFERENCE_PHOTO from "@/graphql/mutations/deleteReferencePhoto.graphql";
import CLOSE_TENDER from "@/graphql/mutations/closeTender.graphql";
import { useUserStore } from "~/stores/user";
import { requestInFlight, trackRequest } from "~/stores/user/inFlightRequests";

const QUERIES = {
  ratings: RATINGS_QUERY,
  priceOffers: PRICE_OFFER_PERMISSION_QUERY,
  questions: QUESTION_SUBSCRIPTIONS_QUERY,
  myPrices: MY_PRICES_QUERY,
  referencePhotos: REFERENCE_PHOTOS_QUERY,
  myTenders: MY_TENDERS_QUERY,
};

const SECTIONS = Object.keys(QUERIES);

const emptySections = () =>
  Object.fromEntries(SECTIONS.map((section) => [section, null]));

const belongsTo = (data, me) =>
  !!data && !!me && String(data.id) === String(me.id);

const sectionOf = (state, section) =>
  belongsTo(state.loaded[section], useUserStore().me)
    ? state.loaded[section]
    : null;

/**
 * Page data of the logged-in person that used to come with `me`, one query
 * per section (`graphql/pageData/*.graphql`), loaded by the pages that show it:
 * - `ratings`: `scores` (my ratings page, price-offer form)
 * - `priceOffers`: `canCreatePriceOffer` (price-offer list and create pages),
 *   refreshed after a price offer is created or duplicated and after a
 *   subscription change
 * - `questions`: `questionSubscriptions` (the subscribe bell of the question
 *   page)
 * - `myPrices`: `prices` and `professionTasks` (my prices page, reloaded on
 *   each visit)
 * - `referencePhotos`: `referencePhotos` (references page, reloaded on each
 *   visit; the price-offer form)
 * - `myTenders`: `tenders` (the customer's "my tenders" page, reloaded on
 *   each visit)
 *
 * A section is returned only while it belongs to the logged-in person.
 */
export const usePageDataStore = defineStore("pageDataStore", {
  state: () => ({
    loaded: emptySections(),
    pending: emptySections(),
  }),
  getters: {
    ratings: (state) => sectionOf(state, "ratings"),
    /**
     * The ratings of the logged-in person, empty until they are loaded.
     * @returns {object[]}
     */
    scores() {
      return this.ratings?.scores ?? [];
    },
    priceOffers: (state) => sectionOf(state, "priceOffers"),
    /**
     * Whether the logged-in person can create another price offer, or null
     * until it is loaded.
     * @returns {boolean | null}
     */
    canCreatePriceOffer() {
      return this.priceOffers ? !!this.priceOffers.canCreatePriceOffer : null;
    },
    questions: (state) => sectionOf(state, "questions"),
    /**
     * The ids of the questions the logged-in person is subscribed to, empty
     * until they are loaded.
     * @returns {string[]}
     */
    subscribedQuestionIds() {
      return (this.questions?.questionSubscriptions ?? []).map(
        (question) => question.id
      );
    },
    myPrices: (state) => sectionOf(state, "myPrices"),
    /**
     * The logged-in person's prices, empty until they are loaded.
     * @returns {object[]}
     */
    prices() {
      return this.myPrices?.prices ?? [];
    },
    /**
     * The profession tasks of the prices page, or null until they are loaded.
     * @returns {object[] | null}
     */
    professionTasks() {
      return this.myPrices?.professionTasks ?? null;
    },
    referencePhotos: (state) => sectionOf(state, "referencePhotos"),
    /**
     * The logged-in person's reference photos, or null until they are loaded.
     * @returns {object[] | null}
     */
    photos() {
      return this.referencePhotos?.referencePhotos ?? null;
    },
    myTenders: (state) => sectionOf(state, "myTenders"),
    /**
     * The tenders the logged-in person requested, or null until they are
     * loaded.
     * @returns {object[] | null}
     */
    tenders() {
      return this.myTenders?.tenders ?? null;
    },
    /**
     * Whether a request for the section is in flight.
     * @returns {(section: string) => boolean}
     */
    isLoading(state) {
      return (section) =>
        !!requestInFlight(this, section, state.pending[section]);
    },
  },
  actions: {
    /**
     * Loads a section once per person, without sending a second request
     * while one is in flight.
     * Requests in flight, also a refresh of loaded data or one that replaced
     * this call's own request, are waited for, so the call never returns with
     * data that is about to be replaced or without the data a newer request
     * brings.
     * @param {string} section
     */
    async load(section) {
      if (!useUserStore().me) {
        return;
      }
      if (
        !this[section] &&
        !requestInFlight(this, section, this.pending[section])
      ) {
        await this.sendQuery(section);
      }
      let pending = requestInFlight(this, section, this.pending[section]);
      while (pending) {
        await pending.catch(() => {});
        pending = requestInFlight(this, section, this.pending[section]);
      }
    },

    /**
     * Reloads a section. A response that arrives after a newer request was
     * sent, or after `setValues`, is dropped.
     * @param {string} section
     */
    async refresh(section) {
      if (!useUserStore().me) {
        return;
      }
      await this.sendQuery(section);
    },

    /**
     * Reloads the sections that are loaded or loading, for callers that
     * changed their data.
     * @param {string[]} [sections]
     */
    async refreshIfLoaded(sections = SECTIONS) {
      await Promise.all(
        sections
          .filter(
            (section) =>
              this[section] ||
              requestInFlight(this, section, this.pending[section])
          )
          .map((section) => this.refresh(section))
      );
    },

    /**
     * Applies changed values to a loaded section.
     * @param {string} section
     * @param {object} values
     */
    setValues(section, values) {
      const data = this[section];
      if (!data) {
        return;
      }
      this.pending[section] = null;
      for (const [key, value] of Object.entries(values)) {
        if (!key.startsWith("__")) {
          Vue.set(data, key, value);
        }
      }
    },

    /**
     * Sets the answer of a loaded rating.
     * @param {{ scoreId: string, answer: object | null }} payload
     */
    setScoreAnswer({ scoreId, answer }) {
      if (!this.ratings) {
        return;
      }
      this.setValues("ratings", {
        scores: this.ratings.scores.map((score) =>
          score.id === scoreId ? { ...score, answer } : score
        ),
      });
    },

    /**
     * Removes an answer from the loaded rating it belongs to.
     * @param {{ answerId: string }} payload
     */
    deleteScoreAnswer({ answerId }) {
      const score = this.ratings?.scores.find(
        (item) => item.answer?.id === answerId
      );
      if (score) {
        this.setScoreAnswer({ scoreId: score.id, answer: null });
      }
    },

    /**
     * Adds a question to the loaded subscriptions.
     * @param {string} questionId
     */
    addQuestionSubscription(questionId) {
      if (!this.questions || this.subscribedQuestionIds.includes(questionId)) {
        return;
      }
      this.setValues("questions", {
        questionSubscriptions: [
          ...this.questions.questionSubscriptions,
          { id: questionId },
        ],
      });
    },

    /**
     * Removes a question from the loaded subscriptions.
     * @param {string} questionId
     */
    removeQuestionSubscription(questionId) {
      if (!this.questions) {
        return;
      }
      this.setValues("questions", {
        questionSubscriptions: this.questions.questionSubscriptions.filter(
          (question) => question.id !== questionId
        ),
      });
    },

    /**
     * Saves a price and puts the result into the loaded prices.
     * @param {object} variables the `createOrUpdatePrice` input
     * @returns {Promise<object>} the saved price
     */
    async createOrUpdatePrice(variables) {
      const result = await this.$nuxt.$mutate(
        CREATE_OR_UPDATE_PRICE,
        variables
      );
      if (this.myPrices) {
        this.setValues("myPrices", {
          prices: [
            ...this.myPrices.prices.filter(
              (price) =>
                !variables.id || String(price.id) !== String(variables.id)
            ),
            result.price,
          ],
        });
      }
      return result.price;
    },

    /**
     * Deletes a price and removes it from the loaded prices.
     * @param {string} priceId
     */
    async deletePrice(priceId) {
      await this.$nuxt.$mutate(DELETE_PRICE, { priceId });
      if (this.myPrices) {
        this.setValues("myPrices", {
          prices: this.myPrices.prices.filter(
            (price) => String(price.id) !== String(priceId)
          ),
        });
      }
    },

    /**
     * Uploads reference photos one by one and adds each to the loaded photos.
     * @param {string[]} fileIds
     * @param {{ id: string } | null} profession
     * @returns {Promise<object[]>} the uploaded photos
     */
    async saveReferencePhotos(fileIds, profession) {
      const uploaded = [];
      for (const fileId of fileIds) {
        const result = await this.$nuxt.$mutate(UPLOAD_REFERENCE_PHOTO, {
          fileId,
          professionId: profession?.id ?? null,
        });
        uploaded.push(result.referencePhoto);
        if (this.photos) {
          this.setValues("referencePhotos", {
            referencePhotos: [...this.photos, result.referencePhoto],
          });
        }
      }
      return uploaded;
    },

    /**
     * Saves a reference photo's note and puts the result into the loaded
     * photos.
     * @param {string} photoId
     * @param {string} note
     * @returns {Promise<object>} the saved photo
     */
    async updateReferencePhoto(photoId, note) {
      const result = await this.$nuxt.$mutate(UPDATE_REFERENCE_PHOTO, {
        photoId,
        note,
      });
      if (this.photos) {
        this.setValues("referencePhotos", {
          referencePhotos: this.photos.map((photo) =>
            photo.id === photoId ? result.referencePhoto : photo
          ),
        });
      }
      return result.referencePhoto;
    },

    /**
     * Deletes a reference photo and removes it from the loaded photos.
     * @param {string} photoId
     */
    async deleteReferencePhoto(photoId) {
      await this.$nuxt.$mutate(DELETE_REFERENCE_PHOTO, { photoId });
      if (this.photos) {
        this.setValues("referencePhotos", {
          referencePhotos: this.photos.filter((photo) => photo.id !== photoId),
        });
      }
    },

    /**
     * Closes one of the logged-in person's tenders and puts the result into
     * the loaded tenders.
     * @param {{ tenderId: string, closeReason: string, winnerPersonTenderId?: string }} variables
     */
    async closeMyTender(variables) {
      let result;
      try {
        result = await this.$nuxt.$mutate(CLOSE_TENDER, variables);
      } catch (error) {
        this.$nuxt.$sentry.captureException(error, {
          tags: {
            action:
              variables.closeReason === "won"
                ? "selectTenderWinnder"
                : "closeTender",
            store: "pageDataStore",
          },
        });
        throw error;
      }
      if (this.tenders) {
        this.setValues("myTenders", {
          tenders: this.tenders.map((tender) =>
            tender.id === variables.tenderId
              ? { ...tender, ...result.tender }
              : tender
          ),
        });
      }
    },

    async sendQuery(section) {
      const userStore = useUserStore();
      const query = this.$nuxt.$query(
        QUERIES[section],
        {},
        { skipErrorPageOnAuthError: true }
      );
      const id = trackRequest(this, section, query);
      this.pending[section] = id;
      try {
        const result = await query;
        if (
          this.pending[section] === id &&
          belongsTo(result?.me, userStore.me)
        ) {
          this.loaded[section] = result.me;
        }
      } catch (error) {
        this.$nuxt.$logger.warn(`Failed to load the ${section} data`, error);
      } finally {
        if (this.pending[section] === id) {
          this.pending[section] = null;
        }
      }
    },
  },
});
